import type { CSSProperties } from 'react'
import type { ImageBlock } from '../lib/types'
import { imageFit } from '../lib/frame'
import { customWidth, imageWidth, liveWidth, PHONE_MIN_WIDTH, wrapOffset } from '../lib/imageSize'

// How an image block sits in the column — its figure's width, float and
// centring, and its photo box's size — shared by the composer (editor/
// BlockViews) and the reader (reader/ArticleBody), so both show readers' view.
//
// At the default width these are the classes image blocks always had:
//   inline  the column at 816×400 (260px tall on phones) until it's freely
//           cropped, then the crop's shape;
//   wraps   the photo at its own shape (or its crop's), 250px wide, floating
//           beside the text from md up (Composer lab's wrap tool, tightened for Niki: 20px to
//           the text, 12px below, stacking down its own side);
//   full    the column at the photo's own (or cropped) shape.
//
// A custom width (lib/imageSize.ts) scales the box rather than cropping more:
//   inline  the band keeps its shape — height scales with the width
//           (400px × w on desktop, 260px × w on phones);
//   wraps   the float is w% of the column, at the photo's shape;
//   shaped  (full, or a cropped inline) keep their own shape.
// Width and height read two CSS variables set on the figure: --wr-w, the
// width in percent, and --wr-wp, the width on phones.
//
// Phones (below md): column images show at max(width, 60%), so a small
// image doesn't shrink to a thumbnail on a 358px column. Wraps already drop
// into the flow there at the column's full width (a 260px band), and stay that
// way whatever their width: their width is their share of a line beside
// the text, and on a phone there's no text beside them.

export interface ImageLayout {
  /** FramedImage's fit. */
  fit: 'cover' | 'shape'
  wrap: boolean
  /** Percent of the column it shows at on desktop (the composer's readout). */
  width: number
  /** Classes for the figure: its width, float or centring. */
  figure: string
  /** Classes for FramedImage's box: its size. */
  box: string
  /** Classes for FramedImage's photo, when unframed at its own shape (a wrap's phone band). */
  img?: string
  /** The CSS variables the width classes read (none at the default width). */
  style?: CSSProperties
}

/** Composer lab's wrap, tightened (Niki, 10/2): 20px to the text, nothing on the outer edge, and a second image on a side stacks below the first. */
const SIDE = {
  'wrap-left': 'md:float-left md:clear-left md:mr-[20px]',
  'wrap-right': 'md:float-right md:clear-right md:ml-[20px]',
} as const

/** Below md a wrap is a 260px band across the column, the photo covering it (centre 60%, as column images rest). */
const PHONE_BAND = 'max-md:h-[260px]'
const PHONE_PHOTO = 'max-md:absolute max-md:inset-0 max-md:size-full max-md:object-cover max-md:object-[center_60%]'

/**
 * A wrap that starts --wr-off lines down the paragraph after it (the hand
 * drops it mid-paragraph): its top margin pushes it down to that line, and
 * its shape leaves the margin out, so the lines above it run the column's
 * full width and only the lines beside the photo wrap. A body line is
 * 1.6 × --wr-body (index.css .wr-t-body), in the composer and the reader alike.
 */
const OFFSET =
  'md:mt-[calc(var(--wr-off)*var(--wr-body)*1.6)] md:[shape-outside:inset(calc(var(--wr-off)*var(--wr-body)*1.6)_0_0_0)]'

/**
 * `cell`: the image is in a column cell (lib/columns.ts). There's no text
 * beside it to wrap, so a wrap shows as a column image, and the band is
 * shorter (220px × width) to suit a cell's ~200–320px.
 *
 * `exact`: draw the width as a percent even at the default (the composer's
 * corner handle mid-drag, unrounded, so the image follows the pointer without a step
 * where the default's own classes take over).
 */
export function imageLayout(
  block: Pick<ImageBlock, 'placement' | 'frame' | 'width' | 'offset'>,
  { cell = false, exact = false }: { cell?: boolean; exact?: boolean } = {},
): ImageLayout {
  if (cell && (block.placement === 'wrap-left' || block.placement === 'wrap-right')) block = { ...block, placement: 'inline' }
  const placement = block.placement ?? 'inline'
  const width = imageWidth(block)
  const custom = exact ? liveWidth(block) : customWidth(block)

  if (placement === 'wrap-left' || placement === 'wrap-right') {
    // Lifted a layer: the rows after a float are positioned and extend under
    // it, so they'd otherwise take its clicks (and paint over the reader's tint).
    const offset = wrapOffset(block)
    const base = `relative z-[1] md:mb-[12px] ${offset ? OFFSET : 'md:mt-0'} ${SIDE[placement]}`
    const off = offset ? ({ '--wr-off': offset } as CSSProperties) : undefined
    const shaped = { fit: 'shape' as const, wrap: true, width, box: `${PHONE_BAND} w-full`, img: PHONE_PHOTO }
    return custom === null
      ? { ...shaped, figure: `${base} md:w-[250px]`, style: off }
      : { ...shaped, figure: `${base} md:w-[calc(var(--wr-w)*1%)]`, style: { ...vars(custom, custom), ...off } }
  }

  const fit = imageFit(placement, block.frame)
  const clear = placement === 'full' ? 'clear-both ' : ''
  if (cell) {
    return custom === null
      ? { fit, wrap: false, width, figure: `${clear}w-full`, box: fit === 'shape' ? 'w-full' : 'h-[220px] w-full' }
      : {
          fit,
          wrap: false,
          width,
          figure: 'clear-both mx-auto w-[calc(var(--wr-w)*1%)]',
          box: fit === 'shape' ? 'w-full' : 'w-full h-[calc(var(--wr-w)*2.2px)]',
          style: vars(custom, custom),
        }
  }
  if (custom === null) {
    return { fit, wrap: false, width, figure: `${clear}w-full`, box: fit === 'shape' ? 'w-full' : 'h-[260px] w-full sm:h-[400px]' }
  }
  return {
    fit,
    wrap: false,
    width,
    // Clear of any float above, so a narrow image is centred in the column rather than squeezed beside it.
    figure: 'clear-both mx-auto w-[calc(var(--wr-wp)*1%)] md:w-[calc(var(--wr-w)*1%)]',
    box:
      fit === 'shape'
        ? 'w-full'
        : 'w-full h-[calc(var(--wr-wp)*2.6px)] sm:h-[calc(var(--wr-wp)*4px)] md:h-[calc(var(--wr-w)*4px)]',
    style: vars(custom, Math.max(custom, PHONE_MIN_WIDTH)),
  }
}

function vars(w: number, phone: number): CSSProperties {
  return { '--wr-w': w, '--wr-wp': phone } as CSSProperties
}
